import { Skeleton, Typography } from "@mui/material";
import ContentDetailPageHeader from "../components/common/content/content-detail-page-header";
import ContentHeader from "../components/common/content/content-header";
import {
  SectionHeading,
  StyledContainer,
} from "../components/common/content/styles";
import { useEffect, useState } from "react";
import { GivingPage } from "../sanity/types";
import { getGivingPage } from "../sanity/queries";
import { fallbackGivingPage } from "../sanity/fallbacks";
import { urlFor } from "../sanity/image";
import {Seo} from "../seo/Seo";
import {AlertToast} from "../components/common/alert-toast";

export default function Giving() {
  const [givingPage, setGivingPage] =
    useState<GivingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let isMounted = true;
    getGivingPage()
      .then((data: GivingPage | null) => {
        if (!isMounted) {
          return;
        }
        if (!data) {
          setGivingPage(fallbackGivingPage);
          return;
        }
        setGivingPage({
          ...fallbackGivingPage,
          ...data,
        });
      })
      .catch(() => {
        if (isMounted) {
          setGivingPage(fallbackGivingPage);
          setErrorMessage("Unable to load giving info. Showing fallback content.");
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const giveOnlineImageUrl = givingPage?.giveOnlineImage
    ? urlFor(givingPage.giveOnlineImage).width(1200).url()
    : givingPage?.giveOnlineImageUrl
    ? process.env.PUBLIC_URL + givingPage.giveOnlineImageUrl
    : "";

  return (
    <>
      <Seo
        title={givingPage?.metaTitle || givingPage?.title || "Giving"}
        description={
          givingPage?.metaDescription ||
          `${givingPage?.mailHeading || "Mail"} | ${givingPage?.inPersonHeading || "In Person at the Church"}`
        }
        path="/giving"
        image={giveOnlineImageUrl}
      />
      <StyledContainer maxWidth={false}>
      <ContentDetailPageHeader/>
      <AlertToast
        open={!!errorMessage}
        message={errorMessage}
        onClose={() => setErrorMessage("")}
        severity="warning"
      />

        {loading ? (
          <>
            <Skeleton variant="text" width="40%" height={38} sx={{mx: "auto", my: 2}} />
            <Skeleton variant="rectangular" width="50%" height={260} sx={{mx: "auto", my: 3}} />
            <Skeleton variant="text" width="30%" height={28} sx={{mx: "auto", my: 2}} />
            <Skeleton variant="text" width="60%" height={24} sx={{mx: "auto"}} />
            <Skeleton variant="text" width="50%" height={24} sx={{mx: "auto"}} />
          </>
        ) : (
          <>
            <ContentHeader title={givingPage?.title || "Giving"} />
            <SectionHeading variant="h5" textAlign={"center"}>
              {givingPage?.giveOnlineHeading || "Give Online"}
            </SectionHeading>
            <div style={{ display: "flex", justifyContent: "center" }}>
              {giveOnlineImageUrl && (
                <img
                  width="50%"
                  src={giveOnlineImageUrl}
                  loading="lazy"
                  alt={
                    givingPage?.giveOnlineImageAlt ||
                    givingPage?.giveOnlineImage?.alt ||
                    "Giving QR-Code"
                  }
                />
              )}
            </div>
            <br />
            <br />
            <SectionHeading variant="h5" textAlign={"center"}>
              {givingPage?.mailHeading || "Mail"}
            </SectionHeading>
            <Typography color="text.primary" variant="body1" textAlign={"center"}>
              <b>
                <u>Please mail your donation to:</u>
              </b>
              <br />
              {(givingPage?.mailAddressLines || fallbackGivingPage.mailAddressLines || []).map(
                (line, index) => (
                  <span key={`${line}-${index}`}>
                    {line}
                    <br />
                  </span>
                )
              )}
            </Typography>
            <br />
            <br />
            <SectionHeading variant="h5" textAlign={"center"}>
              {givingPage?.inPersonHeading || "In Person at the Church"}
            </SectionHeading>
            <Typography color="text.primary" variant="body1" textAlign={"center"}>
              {givingPage?.inPersonText ||
                "Offering boxes are available at the entrance of the church"}
            </Typography>
          </>
        )}
      </StyledContainer>
    </>
  );
}
